import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Flame, MessageCircle, Zap, Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * One-time onboarding tour for first-time users. Shows 4 slides explaining
 * Discover / Groups / Tonight / Chats. Marks `onboarded_at` on the user once
 * they finish or skip so it never reopens.
 */
const SLIDE_DEFS = [
  { icon: Flame,         titleKey: "chats.onboarding.discover_title", bodyKey: "chats.onboarding.discover_body" },
  { icon: Users,         titleKey: "chats.onboarding.groups_title",   bodyKey: "chats.onboarding.groups_body" },
  { icon: Zap,           titleKey: "chats.onboarding.tonight_title",  bodyKey: "chats.onboarding.tonight_body" },
  { icon: MessageCircle, titleKey: "chats.onboarding.chats_title",    bodyKey: "chats.onboarding.chats_body" },
];

export default function OnboardingTour({ user, onClose }) {
  const { t } = useTranslation();
  const SLIDES = SLIDE_DEFS.map((s) => ({ icon: s.icon, title: t(s.titleKey), body: t(s.bodyKey) }));
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Show once-ever: skip if either the server flag OR local-device flag is set.
    let localSeen = false;
    try {
      localSeen = localStorage.getItem("spark_onboarded") === "1";
    } catch { /* private mode */ }
    if (!user.onboarded_at && !localSeen) setOpen(true);
  }, [user]);

  // Auto-dismiss helpers: tapping anywhere outside the slide card closes the tour.
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) finish();
  };

  const finish = async () => {
    setOpen(false);
    // Set local flag synchronously so even if the API call fails, the tour
    // won't reappear on this device.
    try { localStorage.setItem("spark_onboarded", "1"); } catch { /* ignore */ }
    try {
      await api.put("/users/me", { onboarded_at: new Date().toISOString() });
    } catch {
      /* non-blocking */
    }
    onClose?.();
  };

  if (!open) return null;
  const S = SLIDES[i];
  const Icon = S.icon;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-[#16171A]/90 backdrop-blur-md flex items-end sm:items-center justify-center px-4 pb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        data-testid="onboarding-tour"
        onClick={handleBackdropClick}
      >
        <motion.div
          className="w-full max-w-md bg-[#1E1F23] rounded-[28px] border border-[#F0E6D2]/8 overflow-hidden"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C9A961]">
              {t("chats.onboarding.step", { n: i + 1, total: SLIDES.length })}
            </span>
            <button
              onClick={finish}
              className="w-8 h-8 rounded-full bg-[#1B1B1E] border border-[#F0E6D2]/10 flex items-center justify-center active:scale-90"
              data-testid="tour-skip"
              aria-label={t("chats.onboarding.skip")}
            >
              <X className="w-3.5 h-3.5 text-[#8C867C]" />
            </button>
          </div>

          <div className="px-6 py-4 text-center">
            <div
              className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(201,169,97,0.20), rgba(142,111,58,0.10))",
                border: "1px solid rgba(201,169,97,0.30)",
              }}
            >
              <Icon className="w-7 h-7 text-[#C9A961]" />
            </div>
            <h2 className="font-display text-3xl font-light tracking-tight mb-3 text-[#F0E6D2]">
              {S.title}
            </h2>
            <p className="text-[14px] leading-relaxed text-[#C8C2B5] max-w-[32ch] mx-auto">
              {S.body}
            </p>
          </div>

          <div className="flex items-center justify-center gap-1.5 mb-4">
            {SLIDES.map((_, idx) => (
              <span
                key={idx}
                className={`h-1 rounded-full transition-all ${
                  idx === i ? "w-6 bg-[#C9A961]" : "w-1.5 bg-[#F0E6D2]/15"
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2 px-4 pb-4">
            {i > 0 && (
              <button
                onClick={() => setI(i - 1)}
                className="flex-1 bg-transparent border border-[#F0E6D2]/12 rounded-full py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8C867C] active:scale-95"
                data-testid="tour-back"
              >
                {t("chats.onboarding.back")}
              </button>
            )}
            <button
              onClick={() => (i < SLIDES.length - 1 ? setI(i + 1) : finish())}
              className="flex-1 bg-[#C9A961] text-[#16171A] rounded-full py-3 text-[11px] font-semibold uppercase tracking-[0.2em] active:scale-95"
              style={{ boxShadow: "0 0 24px rgba(201,169,97,0.35)" }}
              data-testid="tour-next"
            >
              {i < SLIDES.length - 1 ? t("chats.onboarding.next") : t("chats.onboarding.lets_go")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
